import { selectedNoteTextAtom } from '@/atoms/selectedNoteTextAtoms';
import { useOpenEvidencePanel } from '@/hooks/useOpenEvidencePanel';
import { useSessionStage } from '@/hooks/useSessionStage';
import { useStageAskHeidiQuery } from '@/hooks/useStageAskHeidiQuery';
import { track } from '@/lib/analytics/track';
import {
  composeSelectedTextQuestionPrompt,
  composeSelectionActionPrompt,
  type SelectionAiActionId,
} from '@/lib/selection-ai-prompts';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type Editor } from '@tiptap/react';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { inlineSelectionEditRequestAtom } from './inline-selection-edit/atoms';
import {
  INLINE_EDIT_ACTION_IDS,
  isInlineInsightActionId,
} from './selection-ai-actions.config';
import {
  isSelectionCheckGuidelinesAllowed,
  resolveSelectionActionTarget,
} from './selection-ai-actions.utils';

/**
 * Selection → Ask Heidi routing shared by every selection menu: Check evidence
 * stages an Ask Heidi query, other actions and typed prompts open the inline
 * insight or edit panel.
 */
export const useSelectionAiActionHandlers = ({
  editor,
  onActionStart,
  getTargetRange,
}: {
  editor: Editor;
  onActionStart?: () => void;
  getTargetRange?: () => { from: number; to: number } | null;
}) => {
  const sessionId = useCurrentSessionId();
  const sessionStage = useSessionStage();
  const openEvidencePanel = useOpenEvidencePanel();
  const stageAskHeidiQuery = useStageAskHeidiQuery(sessionId, openEvidencePanel);
  const setSelectedNoteText = useSetAtom(selectedNoteTextAtom);
  const setInlineEditRequest = useSetAtom(inlineSelectionEditRequestAtom);

  const trackSelectionClick = useCallback(
    (action: ButtonAction, label: string, selectionLength: number) => {
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          action,
          label,
          selection_length: selectionLength,
          click_source: 'selection_menu',
          // Empty string off session pages — omit so warehouse joins aren't fabricated.
          session_id: sessionId || undefined,
          session_stage: sessionId ? sessionStage : undefined,
        },
        options: { amplitude: false },
      });
    },
    [sessionId, sessionStage]
  );

  const handleActionClick = useCallback(
    (actionId: SelectionAiActionId) => {
      if (actionId === 'check_guidelines' && !isSelectionCheckGuidelinesAllowed()) {
        return;
      }
      const target = resolveSelectionActionTarget(editor, getTargetRange);
      if (!target) return;
      const { from, to, text } = target;
      onActionStart?.();
      trackSelectionClick(
        ButtonAction.AskHeidiSelectionActionClicked,
        actionId,
        text.length
      );

      // Check evidence needs the full Evidence retrieval stack, so it never runs inline.
      if (actionId !== 'check_guidelines') {
        if (isInlineInsightActionId(actionId)) {
          setInlineEditRequest({ editor, actionId, from, to, text, intent: 'insight' });
          editor.commands.setTextSelection(to);
          return;
        }
        if (INLINE_EDIT_ACTION_IDS.has(actionId)) {
          setInlineEditRequest({ editor, actionId, from, to, text, intent: 'edit' });
          editor.commands.setTextSelection(to);
          return;
        }
      }

      stageAskHeidiQuery({
        message: composeSelectionActionPrompt(actionId, text),
        entryMethod: 'selection_action',
        chipMeta: {
          label: actionId,
          category: 'selection',
          surface: 'selection_menu',
        },
      });
      setSelectedNoteText(null);
      editor.commands.setTextSelection(to);
    },
    [
      editor,
      getTargetRange,
      onActionStart,
      trackSelectionClick,
      setInlineEditRequest,
      stageAskHeidiQuery,
      setSelectedNoteText,
    ]
  );

  const handleAskAnything = useCallback(() => {
    const target = resolveSelectionActionTarget(editor, getTargetRange);
    if (!target) return;
    const { from, to, text } = target;
    onActionStart?.();
    trackSelectionClick(
      ButtonAction.AskHeidiSelectionAskAnythingClicked,
      'ask_anything',
      text.length
    );

    setInlineEditRequest({
      editor,
      actionId: 'ask_anything',
      from,
      to,
      text,
      intent: 'edit',
      promptInput: true,
    });
    editor.commands.setTextSelection(to);
  }, [editor, getTargetRange, onActionStart, trackSelectionClick, setInlineEditRequest]);

  const handleCustomPrompt = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!trimmed) return;
      const target = resolveSelectionActionTarget(editor, getTargetRange);
      if (!target) return;
      const { from, to, text } = target;
      onActionStart?.();
      trackSelectionClick(
        ButtonAction.AskHeidiSelectionAskAnythingClicked,
        'ask_to_edit',
        text.length
      );

      setInlineEditRequest({
        editor,
        actionId: 'ask_anything',
        from,
        to,
        text,
        intent: 'edit',
        customPrompt: composeSelectedTextQuestionPrompt(trimmed, text),
      });
      editor.commands.setTextSelection(to);
    },
    [editor, getTargetRange, onActionStart, trackSelectionClick, setInlineEditRequest]
  );

  return { handleActionClick, handleAskAnything, handleCustomPrompt };
};
